"use client";

import { useState } from "react";
import { AdsAssistantPostMediaTile } from "@/components/ads-assistant/AdsAssistantPostMediaTile";
import type { AdsAssistantPostMediaItem } from "@/lib/adsAssistantClient";
import { deleteAdsSocialPostMedia, uploadManualAdPostImages } from "@/lib/adsAssistantClient";

function originalsOrdered(items: AdsAssistantPostMediaItem[] | undefined): AdsAssistantPostMediaItem[] {
  if (!items?.length) return [];
  return items
    .filter((m) => m.variant === "original")
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
}

function originalsWithSignedUrl(items: AdsAssistantPostMediaItem[]): AdsAssistantPostMediaItem[] {
  return items.filter((m) => typeof m.signed_url === "string" && m.signed_url.length > 0);
}

const MAX_IMAGES_PER_POST = 6;

export function AdsAssistantPostMediaStrip(props: {
  post_media: AdsAssistantPostMediaItem[] | undefined;
  onOpenLightbox: (items: AdsAssistantPostMediaItem[], initialIndex: number) => void;
  onImageError?: () => void;
  /** When set, ingest users can add/remove originals on this post (index list). */
  ingestControls?: {
    postId: string;
    disabled?: boolean;
    onAfterChange: () => void;
  };
  /**
   * Slice 17f-D — when `"video"`, the strip renders a Slice 17f-D video tile
   * (poster + play overlay + Video badge + link-out) instead of an image
   * thumbnail. Other values fall through to existing image / carousel rendering.
   */
  mediaType?: string;
  /** Post-level `thumbnail_url`, used as the video poster (Slice 17f-D). */
  postThumbnailUrl?: string | null;
  /** External platform URL for video tile link-out, or null for a static tile (Slice 17f-D). */
  externalLink?: string | null;
}) {
  const { post_media, onOpenLightbox, onImageError, ingestControls, mediaType, postThumbnailUrl, externalLink } = props;
  const originals = originalsOrdered(post_media);
  const signed = originalsWithSignedUrl(originals);
  const isVideo = mediaType === "video";
  const videoAriaLabel = externalLink ? "Open video on platform in a new tab" : "Video post";
  const [busy, setBusy] = useState<"add" | string | null>(null);

  if (ingestControls) {
    const ic = ingestControls;
    const removing = typeof busy === "string" && busy !== "add" ? busy : null;
    const adding = busy === "add";
    const disabled = !!ic.disabled || !!removing || adding;

    async function onRemove(mediaId: string) {
      if (!window.confirm("Remove this image from the post?")) return;
      setBusy(mediaId);
      const r = await deleteAdsSocialPostMedia(mediaId);
      setBusy(null);
      if (!r.ok) {
        window.alert(r.error);
        return;
      }
      ic.onAfterChange();
    }

    async function onPickFiles(files: FileList | null) {
      if (!files?.length) return;
      setBusy("add");
      const err = await uploadManualAdPostImages(ic.postId, [...files]);
      setBusy(null);
      if (err) {
        window.alert(err);
        return;
      }
      ic.onAfterChange();
    }

    if (isVideo) {
      return (
        <div className="mt-2 flex flex-wrap items-center gap-2" aria-label="Post images">
          <AdsAssistantPostMediaTile
            thumbnailUrl={postThumbnailUrl ?? null}
            ariaLabel={videoAriaLabel}
            onImageError={onImageError}
            video={{ externalLink: externalLink ?? null }}
          />
          <label
            className={
              "inline-flex cursor-pointer items-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-2 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-100 dark:border-zinc-600 dark:bg-zinc-800/80 dark:text-gray-200 dark:hover:bg-zinc-800 " +
              (disabled ? "pointer-events-none opacity-50" : "")
            }
          >
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="sr-only"
              disabled={disabled}
              onChange={(e) => {
                void onPickFiles(e.target.files);
                e.target.value = "";
              }}
            />
            {adding ? "Uploading…" : "Add images"}
          </label>
        </div>
      );
    }

    if (!originals.length) {
      return (
        <div className="mt-2 flex flex-wrap items-center gap-2" aria-label="Post images">
          <label
            className={
              "inline-flex cursor-pointer items-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-2 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-100 dark:border-zinc-600 dark:bg-zinc-800/80 dark:text-gray-200 dark:hover:bg-zinc-800 " +
              (disabled ? "pointer-events-none opacity-50" : "")
            }
          >
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="sr-only"
              disabled={disabled}
              onChange={(e) => {
                void onPickFiles(e.target.files);
                e.target.value = "";
              }}
            />
            {adding ? "Uploading…" : "Add images"}
          </label>
        </div>
      );
    }

    return (
      <div className="mt-2 flex flex-wrap items-center gap-2" aria-label="Post images">
        {originals.map((m, i) => {
          const url = typeof m.signed_url === "string" && m.signed_url.length > 0 ? m.signed_url : null;
          const idxInSigned = url ? signed.findIndex((s) => s.id === m.id) : -1;
          return (
            <AdsAssistantPostMediaTile
              key={m.id}
              thumbnailUrl={url}
              ariaLabel={
                originals.length > 1 ? `Open image ${i + 1} of ${originals.length}` : "Open post image"
              }
              disabled={disabled}
              onClick={
                idxInSigned >= 0 ? () => onOpenLightbox(signed, idxInSigned) : undefined
              }
              onImageError={onImageError}
              removeButton={{
                onClick: () => void onRemove(m.id),
                label: "Remove image",
                disabled,
                pending: removing === m.id,
              }}
            />
          );
        })}
        {originals.length < MAX_IMAGES_PER_POST ? (
          <label
            className={
              "inline-flex cursor-pointer items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-2 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-100 dark:border-zinc-600 dark:bg-zinc-800/80 dark:text-gray-200 dark:hover:bg-zinc-800 " +
              (disabled ? "pointer-events-none opacity-50" : "")
            }
          >
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="sr-only"
              disabled={disabled}
              onChange={(e) => {
                void onPickFiles(e.target.files);
                e.target.value = "";
              }}
            />
            {adding ? "…" : "Add"}
          </label>
        ) : null}
      </div>
    );
  }

  if (isVideo) {
    return (
      <div className="mt-2 flex items-center gap-2">
        <AdsAssistantPostMediaTile
          thumbnailUrl={postThumbnailUrl ?? null}
          ariaLabel={videoAriaLabel}
          onImageError={onImageError}
          video={{ externalLink: externalLink ?? null }}
        />
      </div>
    );
  }

  const extra = signed.length > 1 ? signed.length - 1 : 0;
  const thumb = signed[0];
  if (!originals.length) return null;

  if (!thumb?.signed_url) {
    return (
      <div
        className="mt-2 flex items-center gap-2"
        aria-label="Post has images; refresh the list for a new preview link if expired."
      >
        <div
          className="h-14 w-14 shrink-0 rounded-lg border border-dashed border-gray-300 bg-gray-100 dark:border-zinc-600 dark:bg-zinc-800"
          title="Preview unavailable — signed links expire; use Refresh."
        />
      </div>
    );
  }

  return (
    <div className="mt-2 flex items-center gap-2">
      <AdsAssistantPostMediaTile
        thumbnailUrl={thumb.signed_url}
        ariaLabel={extra ? `Open ${signed.length} post images` : "Open post image"}
        onClick={() => onOpenLightbox(signed, 0)}
        onImageError={onImageError}
        extraCount={extra}
      />
    </div>
  );
}
